import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import {
  RefreshCw,
  Printer,
  FileSpreadsheet,
  FileDown,
  Filter,
  RotateCcw,
  AlertTriangle,
  Info,
  Loader2,
  Inbox
} from 'lucide-react'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import './ContractReports.css'

export const SIGNED_CONTRACT_VIEW = '/contracts/signed-contract/view'

// ---------- formatting ----------

export const toInputDate = (date = new Date()) => {
  const d = new Date(date)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const formatterCache = new Map()
const getFormatter = (key, options) => {
  if (!formatterCache.has(key)) {
    formatterCache.set(key, new Intl.NumberFormat('en-IN', options))
  }
  return formatterCache.get(key)
}

export const formatMoney = (value, currency = 'INR') => {
  const amount = Number(value) || 0
  try {
    return getFormatter(`money-${currency}`, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
  } catch {
    return `${currency} ${getFormatter('num', { maximumFractionDigits: 0 }).format(amount)}`
  }
}

export const formatMoneyCompact = (value, currency = 'INR') => {
  const amount = Number(value) || 0
  try {
    return getFormatter(`compact-${currency}`, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(amount)
  } catch {
    return formatMoney(amount, currency)
  }
}

export const formatNumber = (value) => getFormatter('num', { maximumFractionDigits: 0 }).format(Number(value) || 0)

export const formatPercent = (value) =>
  value === null || value === undefined ? '—' : `${getFormatter('pct', { maximumFractionDigits: 1 }).format(value)}%`

export const formatDate = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export const formatDateTime = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

// Single-currency data shows in that currency; mixed data falls back to INR with a note shown by the page.
export const primaryCurrency = (meta) =>
  meta?.currencies?.length === 1 ? meta.currencies[0] : 'INR'

const STATUS_TONES = {
  Active: 'success',
  Compliant: 'success',
  Approved: 'success',
  Completed: 'success',
  Renewed: 'info',
  'Under Review': 'warning',
  'Pending Approval': 'warning',
  'Legal Review': 'warning',
  'Ready for Signature': 'info',
  Pending: 'warning',
  'In Progress': 'info',
  Suspended: 'warning',
  Draft: 'muted',
  Delayed: 'danger',
  Expired: 'danger',
  Terminated: 'danger',
  Rejected: 'danger',
  'Non-Compliant': 'danger',
  Cancelled: 'muted',
  'Not Applicable': 'muted',
  'Not Set': 'muted'
}

export const StatusBadge = ({ status, tone }) => (
  <span className={`contract-report-badge ${tone || STATUS_TONES[status] || ''}`}>{status || '—'}</span>
)

// ---------- export ----------

const cellValue = (col, row) => {
  if (col.exportValue) return col.exportValue(row)
  const v = row[col.key]
  if (v === null || v === undefined) return ''
  if (v instanceof Date) return formatDate(v)
  return v
}

const csvEscape = (v) => {
  const s = String(v ?? '')
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

const downloadBlob = (content, filename, type) => {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const exportable = (sections) => sections.filter((s) => s && s.columns && s.rows)

export const exportSectionsToCsv = (baseName, sections) => {
  const lines = []
  exportable(sections).forEach((section, idx) => {
    if (idx > 0) lines.push('')
    lines.push(csvEscape(section.title))
    const cols = section.columns.filter((c) => c.export !== false)
    lines.push(cols.map((c) => csvEscape(c.label)).join(','))
    section.rows.forEach((row) => {
      lines.push(cols.map((c) => csvEscape(cellValue(c, row))).join(','))
    })
  })
  downloadBlob(`\uFEFF${lines.join('\r\n')}`, `${baseName}_${toInputDate()}.csv`, 'text/csv;charset=utf-8;')
}

export const exportSectionsToExcel = (baseName, sections) => {
  const wb = XLSX.utils.book_new()
  const usedNames = new Set()
  exportable(sections).forEach((section) => {
    const cols = section.columns.filter((c) => c.export !== false)
    const rows = section.rows.map((row) =>
      Object.fromEntries(cols.map((c) => [c.label, cellValue(c, row)]))
    )
    const ws = rows.length
      ? XLSX.utils.json_to_sheet(rows, { header: cols.map((c) => c.label) })
      : XLSX.utils.aoa_to_sheet([cols.map((c) => c.label)])
    let name = section.title.replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Sheet'
    let n = 2
    while (usedNames.has(name)) name = `${name.slice(0, 28)} ${n++}`
    usedNames.add(name)
    XLSX.utils.book_append_sheet(wb, ws, name)
  })
  XLSX.writeFile(wb, `${baseName}_${toInputDate()}.xlsx`)
}

// ---------- data hook ----------

export const useContractReport = (fetcher, defaults) => {
  const [filters, setFilters] = useState(defaults)
  const [applied, setApplied] = useState(defaults)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async (params) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetcher(params)
      if (response?.success) {
        setData(response.data)
      } else {
        setError(response?.message || 'Failed to load report')
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }, [fetcher])

  useEffect(() => {
    load(applied)
  }, [applied, load])

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }))

  const apply = () => {
    if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
      toast.error('From Date must be on or before To Date')
      return
    }
    setApplied({ ...filters })
  }

  const reset = () => {
    setFilters(defaults)
    setApplied({ ...defaults })
  }

  return { filters, setFilter, applied, apply, reset, refresh: () => load(applied), data, loading, error }
}

// ---------- layout ----------

const FILTER_LABELS = {
  asOf: 'As of Date',
  fromDate: 'From Date',
  toDate: 'To Date'
}

export const ReportShell = ({
  title,
  subtitle,
  path,
  report,
  filterFields = ['asOf'],
  filterHint,
  exportName,
  getExportSections,
  notes = [],
  children
}) => {
  const navigate = useNavigate()
  const { filters, setFilter, apply, reset, refresh, data, loading, error } = report

  const breadcrumbItems = [
    { label: 'Contracts', path: '/contracts', onClick: () => navigate('/contracts') },
    { label: 'Reports', path: '/contracts?tab=reports', onClick: () => navigate('/contracts?tab=reports') },
    { label: title, path, isCurrent: true }
  ]

  const runExport = (kind) => {
    if (!data) return
    try {
      const sections = getExportSections(data)
      if (kind === 'csv') exportSectionsToCsv(exportName, sections)
      else exportSectionsToExcel(exportName, sections)
      toast.success(`${title} exported`)
    } catch (err) {
      console.error('Error exporting report:', err)
      toast.error('Failed to export report')
    }
  }

  const visibleNotes = notes.filter(Boolean)

  return (
    <div className="sales-dashboard contract-report">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header" style={{ textAlign: 'left' }}>
        <div className="page-title" style={{ textAlign: 'left', alignItems: 'flex-start' }}>
          <h1 style={{ fontSize: '22px', margin: '0 0 4px 0' }}>{title}</h1>
          <p style={{ fontSize: '12px', margin: 0 }}>{subtitle}</p>
        </div>
      </div>

      <div className="contract-report-print-title">
        {title} — generated {formatDateTime(data?.meta?.generatedAt || new Date())}
      </div>

      <div className="contract-report-toolbar no-print">
        <div className="contract-report-filters">
          {filterFields.map((field) => (
            <div className="form-group" key={field}>
              <label className="form-label" htmlFor={`report-${field}`}>{FILTER_LABELS[field]}</label>
              <input
                id={`report-${field}`}
                type="date"
                className="form-input"
                value={filters[field] || ''}
                onChange={(e) => setFilter(field, e.target.value)}
              />
            </div>
          ))}
          <button type="button" className="contract-report-btn primary" onClick={apply} disabled={loading} title="Apply filters">
            <Filter size={14} /> Apply
          </button>
          <button type="button" className="contract-report-btn" onClick={reset} disabled={loading} title="Reset filters">
            <RotateCcw size={14} /> Reset
          </button>
        </div>
        <div className="contract-report-actions">
          <button type="button" className="contract-report-btn" onClick={refresh} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'contract-report-spin' : ''} /> Refresh
          </button>
          <button type="button" className="contract-report-btn" onClick={() => window.print()} disabled={!data}>
            <Printer size={14} /> Print
          </button>
          <button type="button" className="contract-report-btn" onClick={() => runExport('csv')} disabled={!data || loading}>
            <FileDown size={14} /> CSV
          </button>
          <button type="button" className="contract-report-btn" onClick={() => runExport('xlsx')} disabled={!data || loading}>
            <FileSpreadsheet size={14} /> Excel
          </button>
        </div>
      </div>

      {data?.meta && (
        <div className="contract-report-meta">
          <span>As of {formatDate(data.meta.asOf)}</span>
          {(data.meta.fromDate || data.meta.toDate) && (
            <span>Period {formatDate(data.meta.fromDate)} – {formatDate(data.meta.toDate)}</span>
          )}
          {filterHint && <span>{filterHint}</span>}
          <span>Live data · generated {formatDateTime(data.meta.generatedAt)}</span>
        </div>
      )}

      {error && !loading && (
        <div className="contract-report-state error">
          <AlertTriangle size={28} />
          <div>{error}</div>
          <button type="button" className="contract-report-btn no-print" onClick={refresh}>
            <RefreshCw size={14} /> Try again
          </button>
        </div>
      )}

      {loading && !data && (
        <div className="contract-report-state">
          <Loader2 size={28} className="contract-report-spin" />
          <div>Loading report…</div>
        </div>
      )}

      {data && !error && (
        <>
          {data.meta?.currencyNote && <ReportNote>{data.meta.currencyNote}</ReportNote>}
          {visibleNotes.map((note, i) => (
            <ReportNote key={i} tone={note.tone}>{note.text || note}</ReportNote>
          ))}
          <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>{children(data)}</div>
        </>
      )}
    </div>
  )
}

export const ReportNote = ({ tone = 'warning', children }) => (
  <div className={`contract-report-note ${tone === 'info' ? 'info' : ''}`}>
    {tone === 'info' ? <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />}
    <span>{children}</span>
  </div>
)

export const EmptyState = ({ message = 'No data for the selected filters' }) => (
  <div className="contract-report-state">
    <Inbox size={28} />
    <div>{message}</div>
  </div>
)

// ---------- building blocks ----------

const KPI_VARIANTS = ['revenue', 'customers', 'churn', 'burn']

export const KpiGrid = ({ items }) => (
  <div className="dashboard-metrics-grid">
    {items.map((item, idx) => {
      const Icon = item.icon
      return (
        <div
          key={item.label}
          className={`metric-card ${item.variant || KPI_VARIANTS[idx % KPI_VARIANTS.length]}`}
          title={item.title}
        >
          <div className="metric-header">
            <div className="metric-icon">{Icon && <Icon size={16} />}</div>
            {item.badge && (
              <div className={`metric-trend ${item.badgeTone || 'neutral'}`}>
                <span>{item.badge}</span>
              </div>
            )}
          </div>
          <div className="metric-value">{item.value}</div>
          <div className="metric-title">{item.label}</div>
          {item.subtitle && <div className="metric-subtitle">{item.subtitle}</div>}
        </div>
      )
    })}
  </div>
)

export const ReportSection = ({ title, subtitle, count, children }) => (
  <div className="contract-report-section">
    <div className="contract-report-section-header">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {count !== undefined && <span className="contract-report-section-count">{count}</span>}
    </div>
    {children}
  </div>
)

export const ReportTable = ({ columns, rows, emptyText = 'No records', footer }) => {
  if (!rows || rows.length === 0) {
    return <div className="contract-report-empty">{emptyText}</div>
  }
  const alignClass = (col) => (col.align ? `align-${col.align}` : undefined)
  return (
    <div className="contract-report-table-wrap">
      <table className="contract-report-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={alignClass(col)}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.id || row._id || idx}>
              {columns.map((col) => (
                <td key={col.key} className={alignClass(col)}>
                  {col.render ? col.render(row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr>
              {columns.map((col) => (
                <td key={col.key} className={alignClass(col)}>{footer[col.key] ?? ''}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

export const ShareBar = ({ value, total }) => {
  const pct = total > 0 ? Math.min(100, Math.max(0, (value / total) * 100)) : 0
  return (
    <div className="contract-report-bar" title={formatPercent(pct)}>
      <span style={{ width: `${pct}%` }} />
    </div>
  )
}

export const ContractLink = ({ id, number, title }) => {
  const navigate = useNavigate()
  if (!id) return <span>{number || '—'}</span>
  return (
    <div>
      <button
        type="button"
        className="contract-report-link"
        onClick={() => navigate(`${SIGNED_CONTRACT_VIEW}?id=${id}`, { state: { contractData: { _id: id } } })}
      >
        {number || 'View'}
      </button>
      {title && <span className="contract-report-subtext">{title}</span>}
    </div>
  )
}

// Reusable column definitions for signed-contract rows returned by the API
export const contractColumns = {
  contract: {
    key: 'contractNumber',
    label: 'Contract',
    render: (r) => <ContractLink id={r.id} number={r.contractNumber} title={r.contractTitle} />,
    exportValue: (r) => r.contractNumber
  },
  title: { key: 'contractTitle', label: 'Title' },
  party: {
    key: 'partyName',
    label: 'Party',
    render: (r) => (
      <div>
        {r.partyName || '—'}
        {r.partyType && <span className="contract-report-subtext">{r.partyType}</span>}
      </div>
    ),
    exportValue: (r) => r.partyName
  },
  type: { key: 'contractType', label: 'Type' },
  status: { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  value: {
    key: 'contractValue',
    label: 'Value',
    align: 'right',
    render: (r) => formatMoney(r.contractValue, r.currency),
    exportValue: (r) => r.contractValue
  },
  currency: { key: 'currency', label: 'Currency' },
  start: {
    key: 'contractStartDate',
    label: 'Start',
    render: (r) => formatDate(r.contractStartDate),
    exportValue: (r) => formatDate(r.contractStartDate)
  },
  end: {
    key: 'contractEndDate',
    label: 'End',
    render: (r) => formatDate(r.contractEndDate),
    exportValue: (r) => formatDate(r.contractEndDate)
  }
}

// Screen tables fold title/currency into other cells; exports list them as separate columns.
export const withExportOnly = (displayCols, exportExtras = []) => ({
  display: displayCols,
  export: [...displayCols.slice(0, 1), ...exportExtras, ...displayCols.slice(1)]
})

// Columns for { [labelKey], count, value } breakdown rows. Share is by value, or by count when all values are 0.
export const breakdownColumns = (labelKey, labelText, rows, currency = 'INR', { badge = false } = {}) => {
  const totalValue = rows.reduce((s, r) => s + (Number(r.value) || 0), 0)
  const totalCount = rows.reduce((s, r) => s + (Number(r.count) || 0), 0)
  const byValue = totalValue > 0
  return [
    {
      key: labelKey,
      label: labelText,
      render: (r) => (badge ? <StatusBadge status={r[labelKey]} /> : r[labelKey])
    },
    { key: 'count', label: 'Count', align: 'right', render: (r) => formatNumber(r.count) },
    {
      key: 'value',
      label: 'Value',
      align: 'right',
      render: (r) => formatMoney(r.value, currency),
      exportValue: (r) => r.value
    },
    {
      key: 'share',
      label: byValue ? 'Share (value)' : 'Share (count)',
      render: (r) => <ShareBar value={byValue ? r.value : r.count} total={byValue ? totalValue : totalCount} />,
      exportValue: (r) => {
        const total = byValue ? totalValue : totalCount
        return total > 0 ? Math.round(((byValue ? r.value : r.count) / total) * 1000) / 10 : 0
      }
    }
  ]
}

export const breakdownFooter = (labelKey, rows, currency = 'INR') => ({
  [labelKey]: 'Total',
  count: formatNumber(rows.reduce((s, r) => s + (Number(r.count) || 0), 0)),
  value: formatMoney(rows.reduce((s, r) => s + (Number(r.value) || 0), 0), currency)
})

export const summarySection = (title, pairs) => ({
  title,
  columns: [
    { key: 'metric', label: 'Metric' },
    { key: 'value', label: 'Value' }
  ],
  rows: pairs.map(([metric, value]) => ({ metric, value: value ?? '' }))
})
